"use client";

import { useCallback, useRef, useState } from "react";
import { Upload as UploadIcon } from "lucide-react";

import {
  UPLOAD_ACCEPT,
  classifyUploadFile,
  isImageFile,
  type ScoreFormat,
} from "@/lib/upload/score-file";

import { type Upload } from "../../molecules/file-drop-zone";

interface StepFileProps {
  /** Currently selected upload (kept for parity with the old API). */
  upload: Upload | null;
  /** A file that encodes the notes: MIDI or MusicXML, same pipeline. */
  onScoreFile: (f: File, format: ScoreFormat) => void;
  onImageFiles: (files: File[]) => void;
  /**
   * Nothing in the selection could be read. The host owns the wording, and
   * needs the files themselves to pick it: a MuseScore project has a way out
   * to name, where an .mp3 has none.
   */
  onUnsupportedFiles: (files: File[]) => void;
  onClear: () => void;
  isLoading: boolean;
  error: string | null;
}

/**
 * "Dépose ta partition" — V2 drop zone for the upload modal.
 *
 * Renders the visual described in the Wave 2D spec (§3.1): a tall dashed
 * card with a violet halo on drag, a circular upload icon, the headline,
 * the supported extensions eyebrow and a secondary "browse" link.
 *
 * The drop-target is exposed as a `<button>` so keyboard users can trigger
 * the file picker via Space/Enter. File detection delegates to
 * `lib/upload/score-file.ts`, the single place that knows which extensions
 * Virtuosa reads.
 */
export function StepFile({
  onScoreFile,
  onImageFiles,
  onUnsupportedFiles,
  isLoading,
  error,
}: StepFileProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback(
    (fileList: FileList | File[]) => {
      const files = Array.from(fileList);

      if (files.length === 0) {
        return;
      }

      // A score file only makes sense alone: it already holds the whole
      // piece, where images are pages that add up to one.
      if (files.length === 1) {
        const kind = classifyUploadFile(files[0]);

        if (kind === "midi" || kind === "musicxml") {
          onScoreFile(files[0], kind);

          return;
        }
      }

      const images = files.filter(isImageFile);

      if (images.length === 0) {
        onUnsupportedFiles(files);

        return;
      }

      onImageFiles(images);
    },
    [onScoreFile, onImageFiles, onUnsupportedFiles],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const handlePick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleFiles(e.target.files);
      }
    },
    [handleFiles],
  );

  const openPicker = useCallback(() => {
    if (isLoading) {
      return;
    }

    inputRef.current?.click();
  }, [isLoading]);

  const dropBorder = isDragging
    ? "var(--color-accent-bright)"
    : "var(--color-border-subtle)";
  const dropBackground = isDragging
    ? "var(--color-violet-soft)"
    : "rgba(255, 255, 255, 0.015)";
  const iconBackground = isDragging
    ? "var(--color-violet-soft)"
    : "rgba(255, 255, 255, 0.04)";
  const iconBorder = isDragging
    ? "var(--color-accent)"
    : "var(--color-border-subtle)";
  const iconColor = isDragging
    ? "var(--color-accent-bright)"
    : "var(--color-text-secondary)";

  return (
    <div className="flex flex-col gap-5 px-6 py-5">
      <button
        type="button"
        onClick={openPicker}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        disabled={isLoading}
        aria-label="Choisir un fichier à importer"
        className="relative flex flex-col items-center justify-center gap-5 overflow-hidden cursor-pointer focus-visible:outline-none"
        style={{
          minHeight: 220,
          borderRadius: "var(--radius-card)",
          border: `1.5px dashed ${dropBorder}`,
          backgroundColor: dropBackground,
          opacity: isLoading ? 0.6 : 1,
          transition: "all 200ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {isDragging && (
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(139, 92, 246, 0.12) 0%, transparent 70%)",
            }}
          />
        )}

        {/* Icon disc */}
        <div
          className="relative z-10 flex items-center justify-center rounded-full"
          style={{
            width: 52,
            height: 52,
            backgroundColor: iconBackground,
            border: `1px solid ${iconBorder}`,
            boxShadow: isDragging
              ? "0 0 24px rgba(139, 92, 246, 0.35)"
              : "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
            transition: "all 200ms cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <UploadIcon
            size={20}
            strokeWidth={1.5}
            aria-hidden
            style={{ color: iconColor }}
          />
        </div>

        {/* Headline + extensions */}
        <div className="relative z-10 flex flex-col items-center gap-1.5 text-center px-4">
          <span
            style={{
              fontSize: 17,
              fontWeight: 500,
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.72) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              color: "transparent",
              letterSpacing: "-0.01em",
            }}
          >
            Dépose ta partition
          </span>
          <span
            className="text-[10px] font-semibold uppercase"
            style={{
              color: "var(--color-text-muted)",
              letterSpacing: "0.18em",
            }}
          >
            MIDI · MUSICXML · PDF · JPEG · PNG
          </span>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept={UPLOAD_ACCEPT}
          multiple
          className="hidden"
          onChange={handlePick}
        />
      </button>

      {/* Secondary links */}
      <div className="flex items-center justify-center">
        <LinkButton label="Parcourir" onClick={openPicker} />
      </div>

      {error && (
        <p
          className="text-[11px] text-center"
          style={{ color: "var(--color-err)" }}
        >
          {error}
        </p>
      )}
    </div>
  );
}

interface LinkButtonProps {
  label: string;
  onClick: () => void;
}

function LinkButton({ label, onClick }: LinkButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[11px] uppercase font-semibold cursor-pointer transition-colors duration-150"
      style={{
        color: "var(--color-text-secondary)",
        letterSpacing: "0.14em",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = "var(--color-accent-bright)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = "var(--color-text-secondary)";
      }}
    >
      {label}
    </button>
  );
}
